/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  GamoraControllerContextIdentifier,
  GamoraFoundationGlueDefaultNamespace,
  GamoraFoundationGlueListenerType,
  GamoraFoundationNodeArgs,
  GamoraUUIDGuard,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakServiceControllerAbortSessionInput,
  HoruzProtocolChulakServiceStateEvent,
  HoruzProtocolChulakServiceStateEventAbortInput,
  HoruzProtocolChulakServiceStateInvoker,
  HoruzProtocolChulakServiceStateYielder,
} from './enums.js';
import { HoruzError } from '../../../../errors/error.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageModule } from '../../modules/message.js';
import { HoruzProtocolChulakMessageSessionAbortedGuard } from '../../guards/message/session-aborted.js';
import { HoruzProtocolChulakServiceStateBase } from './base.js';
import { HoruzProtocolChulakServiceStateContext } from '../context.js';
import { HoruzProtocolChulakServiceStateMap } from '../../statemap.js';
import { IHoruz } from '../../../../horuz.js';
const debug = debugCreate({
  namespace: 'horuz:protocols:chulak:states:server:abort',
});

/**
 * Server
 * @public
 */
export class HoruzProtocolChulakServiceStateAbort extends HoruzProtocolChulakServiceStateBase<
  typeof HoruzProtocolChulakServiceStateMap,
  HoruzProtocolChulakServiceStateContext,
  IHoruz
> {
  constructor(args: GamoraFoundationNodeArgs) {
    super(args);
    this._initialize();
  }

  /**
   * Initialize default listeners
   */
  private _initialize() {
    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.PersistSessionAbortMessage,
      this._onPersistSessionAbortMessageYielder.bind(this),
    );

    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.BroadcastSessionAbortToClients,
      this._onBroadcastSessionAbortToClientsYielder.bind(this),
    );

    this.$registerYield(
      GamoraFoundationGlueDefaultNamespace,
      HoruzProtocolChulakServiceStateYielder.PersistSessionAbortRecoveryFlag,
      this._onPersistSessionAbortRecoveryFlagYielder.bind(this),
    );
  }

  /**
   * Event
   *
   * Persists session abort message to storage (single pod, with lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onPersistSessionAbortMessageYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
    input: HoruzProtocolChulakServiceControllerAbortSessionInput,
  ): Promise<void> => {
    return await this.serviceSessionStore.withLock(
      serviceSessionUuid,
      async () => {
        // Update service session
        const hasServiceSession =
          await this.serviceSessionStore.has(serviceSessionUuid);
        if (hasServiceSession) {
          // Get service session
          const serviceSession =
            await this.serviceSessionStore.get(serviceSessionUuid);

          // Create error guard
          const abortedError =
            HoruzProtocolChulakMessageSessionAbortedGuard.fromJSON({
              code: input.error.code,
              reason: input.error.reason,
            });

          // Set data to service session
          serviceSession.setData(
            HoruzProtocolChulakDataType.DATA_SESSION_ABORTED,
            HoruzProtocolChulakMessageModule.Encode(abortedError),
          );

          // Mark yielder as yielded
          serviceSession.addYielded(
            HoruzProtocolChulakServiceStateYielder.PersistSessionAbortMessage,
          );

          // Store service session
          await this.serviceSessionStore.upsert(serviceSession);
        }
      },
    );
  };

  /**
   * Event
   *
   * Marks recovery flag for reconnecting clients (single pod via sync-session queue, with lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onPersistSessionAbortRecoveryFlagYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    return await this.serviceSessionStore.withLock(
      serviceSessionUuid,
      async () => {
        const hasServiceSession =
          await this.serviceSessionStore.has(serviceSessionUuid);
        if (hasServiceSession) {
          const serviceSession =
            await this.serviceSessionStore.get(serviceSessionUuid);

          // Mark as yielded only if not already marked
          if (
            !serviceSession.hasYielded(
              HoruzProtocolChulakServiceStateYielder.BroadcastSessionAbortToClients,
            )
          ) {
            serviceSession.addYielded(
              HoruzProtocolChulakServiceStateYielder.BroadcastSessionAbortToClients,
            );
            await this.serviceSessionStore.upsert(serviceSession);
          }
        }
      },
    );
  };

  /**
   * Event
   *
   * Broadcasts abort to local WebSocket clients (all pods via notify-session SNS, no lock)
   * @param serviceSessionUuid The session uuid
   */
  private _onBroadcastSessionAbortToClientsYielder = async (
    serviceSessionUuid: GamoraUUIDGuard,
  ): Promise<void> => {
    const hasServiceSession =
      await this.serviceSessionStore.has(serviceSessionUuid);

    // No session, nothing to do
    if (!hasServiceSession) {
      return;
    }

    // Read session data WITHOUT lock (read-only operation)
    const serviceSession =
      await this.serviceSessionStore.get(serviceSessionUuid);

    // Get the abort error message to send to clients
    const abortedErrorRaw = serviceSession.getDataByType(
      HoruzProtocolChulakDataType.DATA_SESSION_ABORTED,
    );
    const abortedError =
      HoruzProtocolChulakMessageModule.Decode<HoruzProtocolChulakMessageSessionAbortedGuard>(
        abortedErrorRaw,
      );

    // Check if THIS pod has any active WebSocket contexts for this session
    const myActiveContextIdentifiers: string[] = [];
    for (const serviceContextIdentifier of serviceSession.contextIdentifiers) {
      if (this.$controller.$hasContext(serviceContextIdentifier)) {
        myActiveContextIdentifiers.push(serviceContextIdentifier);
      }
    }

    // Send abort to OUR active WebSocket contexts (no lock needed)
    // Only pods with local contexts will send
    if (myActiveContextIdentifiers.length > 0) {
      for (const serviceContextIdentifier of myActiveContextIdentifiers) {
        const controllerContext = this.$controller.$getContext(
          serviceContextIdentifier,
        );
        const serviceStateContext = controllerContext.stateContext;

        // Set error
        serviceStateContext.stateContextRegistry.lastError = new HoruzError(
          abortedError.code,
          abortedError.reason,
        );

        // Transition this context to abort
        this.$controller.$abortContext(serviceStateContext.contextIdentifier, {
          stateAbort: 'Abort',
        });
      }
    }
  };

  /**
   * Enter state
   */
  override async $enter(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {
    assert(
      serviceStateContext.stateContextRegistry.serviceSessionUuid,
      'Invalid argument: serviceSessionUuid not available in stateContextRegistry.',
    );
    // Emit abortion handler
    await this.$emit(
      serviceStateContext.contextIdentifier,
      HoruzProtocolChulakServiceStateEvent.Aborted,
      serviceStateContext,
      {
        serviceSessionUuid:
          serviceStateContext.stateContextRegistry.serviceSessionUuid,
        error: serviceStateContext.stateContextRegistry.lastError,
      },
    );
  }

  /**
   * Exit state
   */
  override async $exit(
    serviceStateContext: HoruzProtocolChulakServiceStateContext,
  ): Promise<void> {}

  /**
   * Register invokers
   */
  on(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: typeof HoruzProtocolChulakServiceStateEvent.Aborted,
    listener: (
      stateContext: HoruzProtocolChulakServiceStateContext,
      input: HoruzProtocolChulakServiceStateEventAbortInput,
    ) => Promise<void>,
  ): this;
  on<K extends keyof typeof HoruzProtocolChulakServiceStateInvoker>(
    contextIdentifier: GamoraControllerContextIdentifier,
    name: K,
    listener: GamoraFoundationGlueListenerType,
  ): this {
    return super.$on(contextIdentifier, name, listener);
  }

  /**
   * Register yielders
   * @public
   */
  yielder(
    sessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.PersistSessionAbortMessage,
    input: HoruzProtocolChulakServiceControllerAbortSessionInput,
  ): Promise<void>;
  yielder(
    sessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.BroadcastSessionAbortToClients,
  ): Promise<void>;
  yielder(
    sessionUuid: GamoraUUIDGuard,
    name: typeof HoruzProtocolChulakServiceStateYielder.PersistSessionAbortRecoveryFlag,
  ): Promise<void>;
  yielder<K extends keyof typeof HoruzProtocolChulakServiceStateYielder>(
    sessionUuid: GamoraUUIDGuard,
    name: K,
    ...args: unknown[]
  ): Promise<void> {
    return super.$executeYielder(
      GamoraFoundationGlueDefaultNamespace,
      name,
      sessionUuid,
      ...args,
    );
  }
}
